import type {
  BeginRegistrationDTO,
  FinalizeLoginDTO,
  FinilizeVerifyDTO,
  InitLoginDTO,
  InitRegistrationDTO,
  LoginDTO,
  LoginResult,
  RecoveryVerificationDTO,
  VerificationDTO,
} from '@/types/DTOs';

export abstract class AuthService {
  /* ─── Login / Logout ─── */
  abstract login(data: LoginDTO): Promise<LoginResult>;
  abstract logout(): Promise<void>;
  abstract initLogin(data: { username: string; ke1: string }): Promise<InitLoginDTO>;
  abstract finalizeLogin(data: { sessionId: string; ke3: string }): Promise<FinalizeLoginDTO>;
  abstract forgotPassword(data: { emailAddress: string }): Promise<void>;

  /* ─── Registration wizard ─── */
  abstract beginRegistration(data: {
    username: string;
    otp: string;
  }): Promise<BeginRegistrationDTO>;
  abstract setPassword(data: { username: string; password: string }): Promise<void>;
  abstract confirmEmail(data: { code: string }): Promise<void>;
  abstract sendEmailCode(): Promise<VerificationDTO>;
  abstract resendEmailCode(): Promise<VerificationDTO>;
  abstract confirmPhone(data: { code: string }): Promise<{ message: string }>;
  abstract sendPhoneCode(): Promise<VerificationDTO>;
  abstract resendPhoneCode(): Promise<VerificationDTO>;
  abstract initRegistration(data: {
    username: string;
    registrationRequest: string;
  }): Promise<InitRegistrationDTO>;
  abstract finalizeRegistration(data: {
    sessionId: string;
    registrationRecord: string;
  }): Promise<void>;
  abstract resendRegistrationOtp(data: { username: string }): Promise<void>;

  /* ─── OPAQUE credential rotation (change password) ─── */

  /**
   * OPRF round-trip for a user who already holds a credential. Sibling of
   * `initRegistration`, which rejects existing credentials; the user is
   * resolved from the session, so no username is sent.
   */
  abstract rotateCredentialInit(data: {
    registrationRequest: string;
  }): Promise<InitRegistrationDTO>;

  /**
   * Runs the OPAQUE registration ceremony up to — but not including — the
   * final POST, yielding the two hex strings that message consists of. Lets a
   * caller embed the record in a larger payload (see `finalizeKeyRotation`)
   * instead of sending it to `register/finalize`.
   *
   * `init` is injected so registration keeps using `register/init` while
   * rotation uses `credentials/rotate/init` — the same ceremony, different
   * init endpoint.
   */
  abstract prepareRegistrationRecord(data: {
    username: string;
    password: string;
    init: (
      registrationRequest: string
    ) => Promise<{ sessionId: string; registrationResponse: string }>;
  }): Promise<{ sessionId: string; registrationRecord: string }>;

  /* ─── Two-Factor after password login ─── */
  abstract selectTwoFactorMethod(data: {
    method: 'sms' | 'email';
    twoFactorToken: string;
  }): Promise<VerificationDTO>;
  abstract verifyTwoFactorCode(data: {
    code: string;
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<FinilizeVerifyDTO>;

  abstract verifyRecoveryOtp(data: {
    emailAddress: string;
    code: string;
  }): Promise<RecoveryVerificationDTO>;
  abstract sendRecoveryTwoFactor(data: {
    recoveryToken: string;
    method: 'sms';
  }): Promise<VerificationDTO>;
  abstract verifyRecoveryTwoFactor(data: {
    recoveryToken: string;
    code: string;
    method: 'sms';
  }): Promise<void>;
}
